/**
 * Hides nav entries that point at collections with zero visible products.
 *
 * The birch-data pipeline adds collections to the 'collection-categories'
 * menu before their products are activated; until activation those
 * collections would render as empty pages. This module fetches a
 * handle → visible-product-count map from the menu-visibility section (one
 * Section Rendering API request; a product only counts when published AND
 * in stock, so all-sold-out collections also hide), removes any menu branch
 * whose collections are all empty, and caches the hidden-handle list in
 * localStorage for 24 hours so repeat page views apply the decision without
 * a network request.
 *
 * Covered surfaces (all read the same 'collection-categories' menu): the Shop
 * mega menu, the mobile drawer, the collection sidebar — including the copy
 * the filters block renders into the mobile filters drawer after load, via
 * MutationObserver — and the category tab ribbon on collection pages.
 */
(() => {
  const CACHE_KEY = 'birch:menu-empty-collections:v1';
  const CACHE_TTL = 24 * 60 * 60 * 1000;
  const SCOPE_SELECTOR =
    '.mega-menu__list, .menu-drawer__menu, .collection-sidebar-nav__list, .collection-category-tabs';
  // A mega-menu column <li> can stack several independent branches, each in
  // its own <div>; those need to hide individually, not just whole <li>s. The
  // tab ribbon has no wrapper element per entry — each tab IS its anchor, so
  // it is its own branch and linksIn() has to count the branch itself.
  const BRANCH_SELECTOR = 'li, .mega-menu__column > div, a.collection-category-tab';
  const LINK_SELECTOR = 'a[href*="/collections/"]';
  const HANDLE_RE = /\/collections\/([^/?#]+)/;

  /** @type {Set<string> | null} */
  let hiddenHandles = null;
  let observing = false;

  /** @param {Element} anchor */
  const handleOf = (anchor) => {
    const match = (anchor.getAttribute('href') || '').match(HANDLE_RE);
    return match ? decodeURIComponent(match[1]) : null;
  };

  const rootUrl = () =>
    (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  // A branch is normally a container holding its collection links, but a
  // ribbon tab is itself the link, so the branch has to be included alongside
  // its descendants.
  /** @param {Element} branch */
  const linksIn = (branch) => {
    const descendants = Array.from(branch.querySelectorAll(LINK_SELECTOR));
    return branch.matches(LINK_SELECTOR) ? [branch, ...descendants] : descendants;
  };

  function apply() {
    if (!hiddenHandles || hiddenHandles.size === 0) return;
    let removed = 0;
    for (const scope of document.querySelectorAll(SCOPE_SELECTOR)) {
      for (const branch of scope.querySelectorAll(BRANCH_SELECTOR)) {
        if (!branch.isConnected) continue;
        let sawCollectionLink = false;
        let sawVisibleLink = false;
        for (const anchor of linksIn(branch)) {
          const handle = handleOf(anchor);
          if (!handle) continue;
          sawCollectionLink = true;
          // 'all' and any handle absent from the counts map count as visible,
          // so a branch only disappears when every collection in it is empty.
          if (handle === 'all' || !hiddenHandles.has(handle)) {
            sawVisibleLink = true;
            break;
          }
        }
        // Remove rather than display:none — the mega menu's nth-child divider
        // rules count hidden elements, so a hidden column would leave a stray
        // border at the end of a row.
        if (sawCollectionLink && !sawVisibleLink) {
          branch.remove();
          removed += 1;
        }
      }
    }
    if (removed === 0) return;

    // Removing tabs is enough to fix the ribbon's prev/next arrows —
    // ribbon-scroll.js watches its scroller's child list and re-measures. What
    // it does not handle is a ribbon emptied of every tab, which still occupies
    // its top margin and its arrows, so drop that whole wrapper. (With the
    // "Shop All" tab on — the default — this never triggers: /collections/all
    // always counts as visible.)
    for (const tabs of document.querySelectorAll('.collection-category-tabs')) {
      if (tabs.querySelector('.collection-category-tab')) continue;
      (tabs.closest('[data-ribbon-wrapper]') || tabs).remove();
    }
  }

  // The header refreshes itself through the Section Rendering API and the
  // filters block clones the sidebar nav into the mobile drawer, so nav DOM
  // can (re)appear after first paint. Re-apply when children change; apply()
  // is idempotent, so the mutations from our own removals settle after one
  // extra no-op pass rather than looping.
  function observe() {
    if (observing) return;
    observing = true;
    let scheduled = false;
    new MutationObserver(() => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        apply();
      });
    }).observe(document.body, { childList: true, subtree: true });
  }

  function readCache() {
    try {
      const raw = localStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      const { t, hidden } = JSON.parse(raw);
      if (!Array.isArray(hidden) || Date.now() - t > CACHE_TTL) return null;
      return hidden;
    } catch {
      return null;
    }
  }

  /** @param {string[]} hidden */
  function writeCache(hidden) {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify({ t: Date.now(), hidden }));
    } catch {
      /* private mode / quota — fall back to refetching next page view */
    }
  }

  async function fetchHidden() {
    // priority:'low' keeps this background request from competing with
    // first-load images/fonts; browsers without the option ignore it.
    const response = await fetch(`${rootUrl()}?sections=menu-visibility`, { priority: 'low' });
    if (!response.ok) return null;
    const sections = await response.json();
    if (!sections['menu-visibility']) return null;
    const doc = new DOMParser().parseFromString(sections['menu-visibility'], 'text/html');
    const data = doc.querySelector('[data-menu-collection-counts]');
    if (!data) return null;
    const counts = JSON.parse(data.textContent);
    return Object.keys(counts).filter((handle) => counts[handle] === 0);
  }

  async function init() {
    let hidden = readCache();
    if (hidden === null) {
      try {
        hidden = await fetchHidden();
      } catch {
        return;
      }
      if (hidden === null) return;
      writeCache(hidden);
    }
    if (hidden.length === 0) return;
    hiddenHandles = new Set(hidden);
    apply();
    observe();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
